/**
 * Resolves the hostname of the page embedding the editor iframe.
 *
 * document.referrer is unreliable for this: a strict Referrer-Policy on the
 * host page (or a CDN/proxy in front of it) blanks it, the editor then sends
 * an empty hostname and authorization fails with a 400. Prefer sources the
 * browser never suppresses:
 *
 *  1. origin captured from the parent's postMessage (setEmbedderOrigin)
 *  2. zoid's xprops.getParentDomain() (works in every browser)
 *  3. location.ancestorOrigins (Chromium/Safari)
 *  4. document.referrer as the legacy fallback
 */

interface IZoidWindow {
  xprops?: {
    getParentDomain?: () => string;
  };
}

let embedderOrigin: string | undefined;

const toHost = (origin: string | undefined | null): string | undefined => {
  // Opaque origins serialize to the string "null"
  if (!origin || origin === "null") return undefined;
  try {
    // Hostless URLs like about:blank parse to an empty host
    return new URL(origin).host || undefined;
  } catch {
    return undefined;
  }
};

const zoidParentHost = (): string | undefined => {
  try {
    return toHost((window as IZoidWindow).xprops?.getParentDomain?.());
  } catch {
    return undefined;
  }
};

const ancestorHost = (): string | undefined =>
  // Not implemented in Firefox
  toHost(window.location.ancestorOrigins?.[0]);

export function setEmbedderOrigin(origin: string): void {
  if (toHost(origin)) {
    embedderOrigin = origin;
  }
}

// Returns an empty string when no source is available; callers validate
// before authorization, the API rejects it either way.
export function getEmbedderHostname(): string {
  return (
    toHost(embedderOrigin) ?? zoidParentHost() ?? ancestorHost() ?? toHost(document.referrer) ?? ""
  );
}
